import { useCallback, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import * as authApi from '@/services/auth';
import type { Role } from '@/types';

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const isAuthed = useAuthStore((s) => s.isAuthed);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const setUser = useAuthStore((s) => s.setUser);
  const setBootstrapping = useAuthStore((s) => s.setBootstrapping);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setRoleInStore = useAuthStore((s) => s.setRole);
  const reset = useAuthStore((s) => s.reset);

  useEffect(() => {
    // Use store signal as source of truth: after logout/reset, isBootstrapping
    // flips back to true so we re-fetch me() on next mount.
    if (!useAuthStore.getState().isBootstrapping) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await authApi.me();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) reset();
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reset, setBootstrapping, setUser]);

  const login = useCallback(
    async (phone: string, code: string) => {
      const res = await authApi.verifyOtp(phone, code);
      if (res.accessToken) setAccessToken(res.accessToken);
      setUser(res.user);
      setBootstrapping(false);
      return res.user;
    },
    [setAccessToken, setBootstrapping, setUser]
  );

  const sendOtp = useCallback((phone: string) => authApi.sendOtp(phone), []);
  const requestOtp = sendOtp;

  const verifyOtp = useCallback(
    async (phone: string, code: string) => {
      const res = await authApi.verifyOtp(phone, code);
      if (res.accessToken) setAccessToken(res.accessToken);
      setUser(res.user);
      setBootstrapping(false);
      const u: any = res.user;
      const isNewUser = !u || (u.role !== 'admin' && u.hasProfile !== true);
      return { user: res.user, isNewUser };
    },
    [setAccessToken, setBootstrapping, setUser]
  );

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    reset();
  }, [reset]);

  const refresh = useCallback(async () => {
    const r = await authApi.refresh();
    setAccessToken(r.accessToken);
  }, [setAccessToken]);

  const setRole = useCallback(
    async (role: Role) => {
      const u = await authApi.setRole(role);
      setUser(u);
      setRoleInStore(role);
      return u;
    },
    [setRoleInStore, setUser]
  );

  return { user, isAuthed, isBootstrapping, login, sendOtp, requestOtp, verifyOtp, logout, refresh, setRole };
}
